import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Guide, GuideSummary } from '@/types'

import {
    ArrowLeft,
    ArrowUpRight,
    BadgeCheck,
    ChevronRight,
    Clock,
    LifeBuoy
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { getGuide, getGuideSlugs, getRelatedGuides } from '@/lib/content'
import { useScrollSpy } from '@/hooks/use-scroll-spy'
import RichText from '@/components/cms/rich-text'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/kit/button'
import { FinalCta } from '@/components/kit/final-cta'
import { PageLink } from '@/components/kit/page-link'
import { Reveal } from '@/components/kit/reveal'
import { CONTACT_HREF } from '@/constants/site'

interface GuidePageProps {
    guide: Guide
    related: Array<GuideSummary>
}

const GuidePage: FC<GuidePageProps> = ({ guide, related }): ReactNode => {
    const active = useScrollSpy(guide.toc.map(item => item.id))

    return (
        <Layout>
            <Seo
                title={guide.title}
                description={guide.excerpt ?? `Guía de Himelio: ${guide.title}.`}
                type='article'
            />

            <article aria-labelledby='guide-title'>
                <header className='relative overflow-hidden pb-12 pt-36 md:pb-16 md:pt-44'>
                    <div
                        aria-hidden='true'
                        className='bg-grid mask-radial absolute inset-0'
                    />

                    <div className='relative mx-auto max-w-[1100px] px-5 md:px-10'>
                        <nav aria-label='Ruta de navegación'>
                            <ol className='flex flex-wrap items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-stone'>
                                <li>
                                    <PageLink
                                        href='/docs'
                                        className='transition-colors hover:text-ink'
                                    >
                                        Documentación
                                    </PageLink>
                                </li>
                                {guide.category && (
                                    <>
                                        <li aria-hidden='true'>
                                            <ChevronRight className='size-3.5' />
                                        </li>
                                        <li>
                                            <PageLink
                                                href={`/docs#${guide.category.slug}`}
                                                className='transition-colors hover:text-ink'
                                            >
                                                <span data-mdlfy-cms={`guide_categories:${guide.category.id}:name`}>{guide.category.name}</span>
                                            </PageLink>
                                        </li>
                                    </>
                                )}
                            </ol>
                        </nav>

                        <h1
                            id='guide-title'
                            className='mt-6 max-w-[20ch] text-[clamp(2.5rem,6vw,5rem)] font-bold leading-[0.95] tracking-[-0.04em]'
                        >
                            <span data-mdlfy-cms={`guides:${guide.id}:title`}>{guide.title}</span>
                        </h1>

                        {guide.excerpt && (
                            <p
                                data-mdlfy-cms={`guides:${guide.id}:excerpt`}
                                className='mt-6 max-w-2xl text-lg leading-relaxed text-stone md:text-xl'
                            >
                                {guide.excerpt}
                            </p>
                        )}

                        <div className='mt-8 flex flex-wrap items-center gap-3'>
                            {guide.read_minutes && (
                                <span className='inline-flex items-center gap-2 rounded-full border border-ink/10 bg-paper px-3.5 py-1.5 font-mono text-xs text-ink/80'>
                                    <Clock
                                        aria-hidden='true'
                                        className='size-3.5'
                                    />
                                    <span data-mdlfy-cms={`guides:${guide.id}:read_minutes`}>{guide.read_minutes}</span>
                                    <span> min de lectura</span>
                                </span>
                            )}
                            {guide.plans && (
                                <span className='inline-flex items-center gap-2 rounded-full bg-ember/15 px-3.5 py-1.5 font-mono text-xs text-ink'>
                                    <BadgeCheck
                                        aria-hidden='true'
                                        className='size-3.5 text-ember'
                                    />
                                    <span>Disponible en: </span>
                                    <span data-mdlfy-cms={`guides:${guide.id}:plans`}>{guide.plans}</span>
                                </span>
                            )}
                        </div>
                    </div>
                </header>

                <div className='mx-auto grid max-w-[1100px] gap-12 px-5 md:px-10 lg:grid-cols-[220px_1fr] lg:gap-16'>
                    <aside className='hidden lg:block'>
                        {guide.toc.length > 0 && (
                            <nav
                                aria-label='En esta guía'
                                className='sticky top-28'
                            >
                                <p className='rule-double pt-3 font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>En esta guía</p>
                                <ol className='mt-4 flex flex-col gap-1 border-l border-ink/10'>
                                    {guide.toc.map(item => (
                                        <li key={item.id}>
                                            <a
                                                href={`#${item.id}`}
                                                aria-current={active === item.id ? 'true' : undefined}
                                                className={cn(
                                                    '-ml-px block border-l-2 py-1.5 pl-4 text-sm leading-snug transition-colors',
                                                    active === item.id ? 'border-ember font-semibold text-ink' : 'border-transparent text-stone hover:text-ink'
                                                )}
                                            >
                                                {item.text}
                                            </a>
                                        </li>
                                    ))}
                                </ol>
                            </nav>
                        )}
                    </aside>

                    <div
                        id='article-body'
                        className='min-w-0 max-w-[720px]'
                    >
                        <RichText html={guide.body} />

                        <div className='mt-16 flex flex-col gap-5 rounded-[1.75rem] border border-ink/10 bg-paper p-6 sm:flex-row sm:items-center sm:justify-between md:p-7'>
                            <div className='flex items-start gap-4'>
                                <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-ink text-cream'>
                                    <LifeBuoy className='size-5' />
                                </span>
                                <div>
                                    <p className='font-semibold'>¿Te quedó alguna duda?</p>
                                    <p className='mt-1 text-sm text-stone'>Abre un ticket y el equipo de Himelio te responde por correo.</p>
                                </div>
                            </div>
                            <Button
                                href={CONTACT_HREF}
                                variant='dark'
                                size='sm'
                            >
                                Contactar soporte
                            </Button>
                        </div>

                        <PageLink
                            href='/docs'
                            className='group mt-8 inline-flex items-center gap-2 text-[15px] font-semibold'
                        >
                            <ArrowLeft className='size-4 transition-transform duration-500 ease-expo group-hover:-translate-x-1' />
                            Todas las guías
                        </PageLink>
                    </div>
                </div>
            </article>

            {related.length > 0 && (
                <section
                    aria-labelledby='related-title'
                    className='mx-auto max-w-[1400px] px-5 pt-24 md:px-10 md:pt-32'
                >
                    <h2
                        id='related-title'
                        className='rule-double pt-4 text-[clamp(2rem,4vw,3.25rem)] font-bold leading-none tracking-[-0.035em]'
                    >
                        Sigue <span className='font-serif font-normal italic tracking-[-0.01em]'>leyendo</span>
                    </h2>
                    <ul className='mt-8 grid gap-3 md:grid-cols-3'>
                        {related.map((item, index) => (
                            <Reveal
                                key={item.id}
                                as='li'
                                delay={index * 80}
                            >
                                <PageLink
                                    href={`/docs/${item.slug}`}
                                    className='group flex h-full flex-col gap-3 rounded-[1.5rem] border border-ink/10 bg-paper p-6 transition-colors hover:border-ink/15'
                                >
                                    {item.category && (
                                        <span
                                            data-mdlfy-cms={`guide_categories:${item.category.id}:name`}
                                            className='font-mono text-[11px] uppercase tracking-[0.14em] text-stone'
                                        >
                                            {item.category.name}
                                        </span>
                                    )}
                                    <h3
                                        data-mdlfy-cms={`guides:${item.id}:title`}
                                        className='text-xl font-bold leading-tight tracking-[-0.025em] transition-colors group-hover:text-ember'
                                    >
                                        {item.title}
                                    </h3>
                                    {item.excerpt && (
                                        <p
                                            data-mdlfy-cms={`guides:${item.id}:excerpt`}
                                            className='line-clamp-2 text-[15px] leading-relaxed text-stone'
                                        >
                                            {item.excerpt}
                                        </p>
                                    )}
                                    <span className='mt-auto flex items-center gap-1.5 pt-2 text-sm font-semibold'>
                                        Leer la guía
                                        <ArrowUpRight className='size-4 transition-transform duration-500 ease-expo group-hover:rotate-45' />
                                    </span>
                                </PageLink>
                            </Reveal>
                        ))}
                    </ul>
                </section>
            )}

            <FinalCta />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getGuideSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Las rutas de las guías no se pudieron cargar:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<GuidePageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true }

    try {
        const guide = await getGuide(slug)

        if (!guide) return { notFound: true, revalidate: 60 }

        const related = await getRelatedGuides(guide, 3)

        return { props: { guide, related }, revalidate: 60 }
    }
    catch (error) {
        console.error('La guía no se pudo cargar:', error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 30 }
    }
}

export default GuidePage
